import axios from 'axios';
import { createVideoUploadApi } from '@/api/videoUpload';
import { isFileUid } from '@/utils/fileUrl';
import {
  NO_PROGRESS_MS,
  chunkLimits,
  etaSeconds,
  failedSessionCode,
  initialUploadState,
  isUploadInFlight,
  nextChunkSize,
  progressPercent,
  reduceUploadState,
  resumeKey,
  retryDelayMs,
  updateThroughput,
  uploadChipActions,
  uploadStateText,
} from '@/utils/videoUploadPlan';

/**
 * One video, from the picked File to a finished upload keyon holds, in chunks
 * (keyon/video_upload.php). It runs the requests and the timers; every decision
 * it can make without a browser - the chunk size, the waits, the states, the
 * sentences - is videoUploadPlan.js's.
 *
 * What it survives, without the person doing anything:
 *   - a slow link: chunks follow the speed (about eight seconds each);
 *   - a chunk that stalls (20 s without progress), a dropped connection or a
 *     server that is busy: up to six waits of 1 to 30 s, each followed by
 *     asking the server where it is (status), so a chunk that did arrive is
 *     never sent twice and one that did not is sent from the right place;
 *   - the network going away: the chunk in flight is dropped, nothing is sent
 *     until it is back, and the wait does not count as a failure;
 *   - a lapsed second factor: keyon answers 401 mfa_required, the upload parks
 *     (it is NOT the global handler's redirect, which would lose the message
 *     being written), and "Verify again" opens the code page in another window;
 *     coming back to this one (focus, or the tab shown) and a status call that
 *     succeeds carries on at the stored offset;
 *   - a session keyon has lost (upload_state_lost, expired, aborted): it begins
 *     one new one, once. A session keyon says has failed is final, never begun
 *     again on its own: that would send the whole video a second time.
 * After that it stops and says so, and `retry()` begins again (unless the
 * failure is a verdict a retry cannot change); the same resume key means keyon
 * carries on a session that is still open.
 *
 * Everything outside the page is injectable, so a test decides what time it is,
 * what the network does and what the browser can do.
 */

// A status, a start or a probe that gets no answer for this long is abandoned
// like a stalled chunk. A complete is given the whole of keyon's lease: it
// commits the blocks and reads the file back before it answers.
const CALL_TIMEOUT_MS = NO_PROGRESS_MS;
const COMPLETE_TIMEOUT_MS = 120000;
// Waiting for another request on the same session to finish is not a failure,
// but it is not forever either: keyon's lease is two minutes.
const MAX_BUSY_WAITS = 90;
const DEFAULT_BUSY_WAIT_S = 2;
// A server that keeps saying "not that offset" is not going to say anything else.
const MAX_ORDER_FIXES = 20;
// The code page, as mfaRequired.js names it.
const CODE_PAGE_PATH = '/sixDigitAuthorization';

// Control flow, not an error: the upload was cancelled, or has already failed
// (the failed state is set before this is thrown).
class Stop {
  constructor(kind) {
    this.kind = kind;
  }
}

function defaultTimers() {
  return {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
    now: () => Date.now(),
  };
}

function defaultEnv() {
  if (typeof window === 'undefined') return { window: null, document: null, navigator: null };
  return { window, document, navigator };
}

/**
 * The code page as a route of this page: the router is hash-based
 * (createWebHashHistory), so it is `<this page>#/sixDigitAuthorization`, and
 * neither this page's query nor its own hash is carried over.
 */
export function codePageUrl(location) {
  return `${location.origin}${location.pathname}#${CODE_PAGE_PATH}`;
}

// The new window needs the signed-in person's page state (sessionStorage), which
// a window.open copies and a noopener one does not: without it the code page
// has nobody to verify. So no noopener here, deliberately.
function defaultOpenVerifyWindow() {
  if (typeof window !== 'undefined') window.open(codePageUrl(window.location), '_blank');
}

async function readBytes(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

const hex = (buffer) => Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');

// crypto.subtle exists only in a secure context (https, or localhost).
async function subtleSha256Blob(subtle, blob) {
  return hex(await subtle.digest('SHA-256', await readBytes(blob)));
}

async function subtleSha256Text(subtle, text) {
  return hex(await subtle.digest('SHA-256', new TextEncoder().encode(text)));
}

function wholeNumber(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && /^[0-9]{1,15}$/.test(value)) return Number(value);
  return null;
}

/** A refusal that says video is off on the server: final, where other 503s are worth waiting out. */
function isSwitchedOff(res) {
  return res.code === 'uploads_unavailable' && (res.reason === 'class_disabled' || res.reason === 'not_wired');
}

/**
 * What an answer means for the call that got it:
 *   ok         it worked
 *   mfa        the second factor lapsed: park
 *   transient  the request's outcome is unknown or the server is struggling: wait and ask where it is
 *   refused    an answer about this request, for the caller to read
 */
function classify(res) {
  if (res.ok) return 'ok';
  if (res.network) return 'transient';
  if (res.status === 401 && res.code === 'mfa_required') return 'mfa';
  // A 2xx that is not keyon's JSON is a proxy's page, not an answer.
  if (res.status >= 200 && res.status < 300) return 'transient';
  if (res.status >= 500) return isSwitchedOff(res) ? 'refused' : 'transient';
  if (res.status === 429) return res.code === null || res.code === 'rate_limited' ? 'transient' : 'refused';
  if (res.code === 'chunk_corrupt' || res.code === 'payload_too_large') return 'transient';
  return 'refused';
}

/**
 * @param {object} options
 * @param {File} options.file the picked video
 * @param {string} options.uid the person (part of the resume key)
 * @param {object} options.policy GET video_upload.php?action=config's answer
 * @param {string} [options.domain] message (the default) or note
 * @param {string} [options.patientId] the patient a note's video is for (domain note)
 * @param {object} [options.api] createVideoUploadApi(); or `client` to make one
 * @param {(blob: Blob) => Promise<string>} [options.sha256Blob]
 * @param {(text: string) => Promise<string>} [options.sha256Text]
 * @param {object} [options.crypto] where crypto.subtle is, when no hash function is given
 * @param {{setTimeout, clearTimeout, now}} [options.timers]
 * @param {() => number} [options.random]
 * @param {{window, document, navigator}} [options.env]
 * @param {{request: Function}} [options.wakeLock]
 * @param {() => void} [options.openVerifyWindow]
 */
export function createVideoUploader(options) {
  const { file, uid, policy, domain = 'message', patientId } = options;
  const api = options.api || createVideoUploadApi(options.client ? { client: options.client } : {});
  const timers = options.timers || defaultTimers();
  const random = options.random || Math.random;
  const env = options.env || defaultEnv();
  const wakeLock = options.wakeLock !== undefined ? options.wakeLock : (typeof navigator !== 'undefined' ? navigator.wakeLock : undefined);
  const openVerifyWindow = options.openVerifyWindow || defaultOpenVerifyWindow;
  const cryptoApi = options.crypto !== undefined ? options.crypto : (typeof globalThis !== 'undefined' ? globalThis.crypto : null);
  const subtle = cryptoApi ? cryptoApi.subtle : null;
  const sha256Blob = options.sha256Blob || (subtle ? (blob) => subtleSha256Blob(subtle, blob) : null);
  const sha256Text = options.sha256Text || (subtle ? (text) => subtleSha256Text(subtle, text) : null);
  const limits = chunkLimits(policy);
  const total = file.size;

  let state = initialUploadState();
  let snapshot = null;
  let snapshotKey = '';
  const listeners = new Set();

  // Where the upload is, kept apart from the state: the state says what it is
  // doing, this says how far it got.
  const ctx = {
    uploadId: null,
    key: null,
    offset: 0,
    chunkSize: limits.initial,
    ema: null,
    failures: 0,
    restarts: 0,
    busyWaits: 0,
    orderFixes: 0,
    lastFailure: null,
    completed: null,
    pendingStatus: null,
    inChunk: null,
  };

  let running = null;
  let cancelled = false;
  let released = false;
  let discarded = false;
  let attached = false;
  let offline = false;
  let inflight = null;
  let sleeper = null;
  let gate = null;
  let probing = false;
  let sentinel = null;

  // ---- state and what a page reads of it ----------------------------------

  function buildSnapshot() {
    const done = state.status === 'ready' || state.status === 'finalizing';
    const inChunk = ctx.inChunk ? ctx.inChunk.length * ctx.inChunk.fraction : 0;
    const sent = done ? total : Math.min(total, ctx.offset + inChunk);
    const percent = done ? 100 : progressPercent(sent, total);
    const eta = state.status === 'uploading' ? etaSeconds(total - sent, ctx.ema) : null;
    return {
      status: state.status,
      code: state.code,
      message: state.message,
      fileUid: state.fileUid,
      text: uploadStateText(state, { percent, etaSeconds: eta }),
      percent,
      etaSeconds: eta,
      sentBytes: Math.floor(sent),
      totalBytes: total,
      actions: uploadChipActions(state.status, state.code),
      name: file.name,
      size: total,
    };
  }

  // Told to the page only when something it shows changed: a request reports
  // progress many times a second, and a page does not need most of them.
  function refresh() {
    const next = buildSnapshot();
    const key = `${next.status}|${next.code}|${next.message}|${next.percent}|${next.text}|${next.fileUid}`;
    if (key === snapshotKey) return;
    snapshot = next;
    snapshotKey = key;
    listeners.forEach((listener) => listener(snapshot));
  }

  function dispatch(event) {
    const next = reduceUploadState(state, event);
    if (next === state) return;
    state = next;
    refresh();
  }

  function fail(code, message = null) {
    dispatch({ type: 'fail', code, message });
    return new Stop('failed');
  }

  function stopIfCancelled() {
    if (cancelled) throw new Stop('cancelled');
  }

  // ---- waiting ------------------------------------------------------------

  function newGate() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  }

  function sleep(ms) {
    return new Promise((resolve, reject) => {
      if (cancelled) {
        reject(new Stop('cancelled'));
        return;
      }
      const id = timers.setTimeout(() => {
        sleeper = null;
        resolve();
      }, ms);
      sleeper = { id, reject };
    });
  }

  async function waitUntilOnline() {
    while (offline) {
      stopIfCancelled();
      gate = newGate();
      await gate.promise;
    }
  }

  // The wait before the next try of a failed call. The sixth is the last: then the person is asked.
  async function backoff() {
    ctx.failures += 1;
    const delay = retryDelayMs(ctx.failures - 1, random);
    if (delay === null) {
      const last = ctx.lastFailure || {};
      throw fail(last.code || 'network', last.message || null);
    }
    await sleep(delay);
  }

  // ---- one request --------------------------------------------------------

  /**
   * One call with a timer behind it: nothing heard for `timeoutMs` (any
   * progress of an upload counts) abandons it. Progress is `onProgress`'s, for
   * the caller that has any to report.
   */
  async function timed(send, timeoutMs, onFraction) {
    const call = { source: axios.CancelToken.source(), reason: null, timer: null, done: false };
    inflight = call;
    const disarm = () => {
      if (call.timer !== null) timers.clearTimeout(call.timer);
      call.timer = null;
    };
    const arm = () => {
      disarm();
      call.timer = timers.setTimeout(() => abort(call, 'stall'), timeoutMs);
    };
    const onProgress = (event) => {
      if (call.done) return;
      arm();
      if (onFraction && event && event.total > 0) onFraction(Math.min(1, Math.max(0, event.loaded / event.total)));
    };
    arm();
    let res;
    try {
      res = await send(call.source.token, onProgress);
    } finally {
      disarm();
      call.done = true;
      if (inflight === call) inflight = null;
    }
    return { res, reason: call.reason };
  }

  function abort(call, reason) {
    if (call.done || call.reason) return;
    call.reason = reason;
    call.source.cancel(reason);
  }

  /**
   * Makes the call until it has an answer worth giving to the caller:
   *   {kind:'ok', res}       it worked
   *   {kind:'refused', res}  an answer about this request (409, a verdict, ...)
   *   {kind:'retry', counted} the outcome is unknown, or the server is struggling;
   *                          the caller asks where the server is (counted: wait first)
   * and handles what is the same for every call: waiting for the network
   * before a send, parking on a lapsed second factor, and cancellation.
   */
  async function attempt(send, { timeoutMs = CALL_TIMEOUT_MS, onFraction } = {}) {
    stopIfCancelled();
    await waitUntilOnline();
    stopIfCancelled();
    const { res, reason } = await timed(send, timeoutMs, onFraction);
    if (cancelled) {
      // A session that was begun as the person cancelled is still theirs to release.
      if (res.ok && res.data && isFileUid(res.data.upload_id) && !ctx.uploadId) ctx.uploadId = res.data.upload_id;
      throw new Stop('cancelled');
    }
    if (res.network) {
      if (reason === 'offline') return { kind: 'retry', counted: false };
      ctx.lastFailure = { code: 'network', message: null };
      return { kind: 'retry', counted: true };
    }
    switch (classify(res)) {
      case 'ok':
        return { kind: 'ok', res };
      case 'mfa':
        await park();
        return { kind: 'retry', counted: false };
      case 'transient':
        ctx.lastFailure = { code: res.code || 'network', message: res.message };
        return { kind: 'retry', counted: true };
      default:
        return { kind: 'refused', res };
    }
  }

  // ---- a lapsed second factor ---------------------------------------------

  async function park() {
    dispatch({ type: 'mfa_lapsed' });
    gate = newGate();
    await gate.promise;
    dispatch({ type: 'mfa_resumed' });
  }

  // Back in this window: has the person verified? The probe is a status call
  // (or the config before there is a session), and only an answer that is not
  // a refusal lets the upload go on. It resumes at what the server holds.
  async function tryResume() {
    if (state.status !== 'paused_mfa' || probing || cancelled) return;
    probing = true;
    try {
      let verified = false;
      if (ctx.uploadId) {
        const { res } = await timed((token) => api.status(ctx.uploadId, { cancelToken: token }), CALL_TIMEOUT_MS);
        if (res.ok) {
          ctx.pendingStatus = res;
          verified = true;
        }
      } else {
        const { res } = await timed(async (token) => ({ ok: !!(await api.config({ cancelToken: token })) }), CALL_TIMEOUT_MS);
        verified = res.ok === true;
      }
      if (verified && gate && state.status === 'paused_mfa') gate.resolve();
    } catch (_) {
      // Still parked; the next focus asks again.
    } finally {
      probing = false;
    }
  }

  // ---- where the server is ------------------------------------------------

  // Takes what a status answer says. Returns 'lost' when the session cannot be
  // carried on but a new one may be begun: keyon aborted it (a restart, a
  // discard, a lost state) or it ran out of time. A failed session, or a status
  // this page does not know, is final (failedSessionCode): keyon failed it for a
  // reason a new session would meet again, after sending the whole file again.
  function adoptStatus(data) {
    if (data.status === 'complete') {
      ctx.completed = { fileUid: isFileUid(data.file_uid) ? data.file_uid : ctx.uploadId };
      return null;
    }
    if (data.status === 'aborted' || data.status === 'expired') return 'lost';
    if (data.status !== 'open') throw fail(failedSessionCode(data.status_reason));
    const next = wholeNumber(data.next_offset);
    if (next === null || next > total) return 'lost';
    ctx.offset = next;
    return null;
  }

  async function restartSession(res) {
    if (ctx.restarts >= 1) throw fail(res.code || 'upload_state_lost', res.message);
    ctx.restarts += 1;
    // begin() takes the new session's offset (0, unless keyon carries on one it finds).
    ctx.uploadId = null;
    ctx.completed = null;
    ctx.pendingStatus = null;
    ctx.failures = 0;
    ctx.chunkSize = limits.initial;
    await begin();
    dispatch({ type: 'resend' });
  }

  // The outcome of the last request is not known: ask the server where it is
  // and carry on from there, whether that is the same chunk, the next one or
  // the end.
  async function resync() {
    for (;;) {
      let answer = ctx.pendingStatus;
      ctx.pendingStatus = null;
      if (!answer) {
        const out = await attempt((token) => api.status(ctx.uploadId, { cancelToken: token }));
        if (out.kind === 'retry') {
          if (out.counted) await backoff();
          continue;
        }
        if (out.kind === 'refused') {
          await refusedSession(out.res);
          return;
        }
        answer = out.res;
      }
      if (adoptStatus(answer.data) === 'lost') {
        await restartSession({ code: 'upload_state_lost', message: null });
      }
      return;
    }
  }

  // A refusal that says this session is gone, or something that ends the upload.
  async function refusedSession(res) {
    const gone = res.status === 404 || res.status === 410 || res.code === 'upload_state_lost' || res.code === 'upload_session_expired';
    if (gone) {
      await restartSession(res);
      return;
    }
    throw fail(res.code || 'unknown', res.message);
  }

  // ---- the three phases ---------------------------------------------------

  async function begin() {
    if (ctx.key === null) ctx.key = await resumeKey(file, uid, sha256Text);
    for (;;) {
      const out = await attempt((token) => api.start({
        domain,
        patientId,
        filename: file.name,
        size: total,
        chunkSize: ctx.chunkSize,
        resumeKey: ctx.key,
      }, { cancelToken: token }));
      if (out.kind === 'ok') {
        const id = out.res.data.upload_id;
        const offset = wholeNumber(out.res.data.next_offset);
        if (!isFileUid(id) || offset === null || offset > total) throw fail('unknown');
        ctx.uploadId = id;
        ctx.offset = offset;
        return;
      }
      if (out.kind === 'refused') {
        // At start a 404 not_found is not a lost session (there is none yet):
        // for a note it is NoteWriteGate saying the patient is not in this account.
        if (domain === 'note' && out.res.status === 404 && out.res.code === 'not_found') throw fail('patient_not_found');
        throw fail(out.res.code || 'unknown', out.res.message);
      }
      if (out.counted) await backoff();
    }
  }

  function acked(res, length, ms) {
    const next = wholeNumber(res.data.next_offset);
    ctx.offset = next !== null && next <= total ? next : ctx.offset + length;
    ctx.failures = 0;
    ctx.busyWaits = 0;
    ctx.orderFixes = 0;
    ctx.inChunk = null;
    // A chunk that was already there crossed no wire: it says nothing about the speed.
    if (!res.data.duplicate) {
      ctx.ema = updateThroughput(ctx.ema, length, ms);
      ctx.chunkSize = nextChunkSize({ size: length, bytes: length, ms }, limits);
    }
    refresh();
  }

  async function sendChunks() {
    while (ctx.offset < total && !ctx.completed) {
      stopIfCancelled();
      const offset = ctx.offset;
      const length = Math.min(ctx.chunkSize, total - offset);
      const blob = file.slice(offset, offset + length);
      let sha;
      try {
        sha = await sha256Blob(blob);
      } catch (_) {
        throw fail('file_unreadable');
      }
      stopIfCancelled();
      ctx.inChunk = { length, fraction: 0 };
      // Timed from the moment the request goes out, not from before a wait for the network.
      let startedAt = timers.now();
      const out = await attempt(
        (token, onProgress) => {
          startedAt = timers.now();
          return api.chunk({ uploadId: ctx.uploadId, offset, sha256: sha, blob }, { cancelToken: token, onProgress });
        },
        { onFraction: (fraction) => { if (ctx.inChunk) { ctx.inChunk.fraction = fraction; refresh(); } } }
      );
      if (out.kind === 'ok') {
        acked(out.res, length, timers.now() - startedAt);
      } else if (out.kind === 'retry') {
        ctx.inChunk = null;
        if (out.counted) {
          // A chunk that failed is sent again smaller: the link could not carry it.
          ctx.chunkSize = nextChunkSize({ size: length, failed: true }, limits);
          await backoff();
        }
        await resync();
      } else {
        ctx.inChunk = null;
        await refusedChunk(out.res);
      }
    }
  }

  async function refusedChunk(res) {
    if (res.code === 'out_of_order') {
      ctx.orderFixes += 1;
      if (ctx.orderFixes > MAX_ORDER_FIXES) throw fail('out_of_order', res.message);
      // The reply says where the session is: carry on from there, at once.
      if (res.nextOffset !== null && res.nextOffset <= total) {
        ctx.offset = res.nextOffset;
        return;
      }
      await resync();
      return;
    }
    if (res.code === 'upload_busy') {
      ctx.busyWaits += 1;
      if (ctx.busyWaits > MAX_BUSY_WAITS) throw fail('upload_busy', res.message);
      await sleep((res.retryAfter !== null ? res.retryAfter : DEFAULT_BUSY_WAIT_S) * 1000);
      return;
    }
    await refusedSession(res);
  }

  // The commit. Returns 'done' when the file is ready, 'more' when the server says it is short of the end.
  async function finalize() {
    for (;;) {
      const out = await attempt((token) => api.complete(ctx.uploadId, { cancelToken: token }), { timeoutMs: COMPLETE_TIMEOUT_MS });
      if (out.kind === 'ok') {
        const fileUid = out.res.data.file_uid;
        if (!isFileUid(fileUid)) throw fail('unknown');
        ctx.completed = { fileUid };
        return 'done';
      }
      if (out.kind === 'retry') {
        if (out.counted) await backoff();
        await resync();
        if (ctx.completed) return 'done';
        if (ctx.offset < total) return 'more';
        continue;
      }
      const { res } = out;
      if (res.code === 'incomplete_upload') {
        if (res.nextOffset === null || res.nextOffset > total) throw fail('incomplete_upload', res.message);
        ctx.offset = res.nextOffset;
        return 'more';
      }
      if (res.code === 'upload_busy') {
        ctx.busyWaits += 1;
        if (ctx.busyWaits > MAX_BUSY_WAITS) throw fail('upload_busy', res.message);
        await sleep((res.retryAfter !== null ? res.retryAfter : DEFAULT_BUSY_WAIT_S) * 1000);
        continue;
      }
      await refusedSession(res);
      return 'more';
    }
  }

  async function runFlow() {
    if (!sha256Blob || !sha256Text) throw fail('no_crypto');
    dispatch({ type: 'validated' });
    // Cancelled while not yet begun, or no network to begin with.
    if (offline) dispatch({ type: 'offline' });
    await begin();
    dispatch({ type: 'started' });
    for (;;) {
      await sendChunks();
      dispatch({ type: 'all_sent' });
      if (!ctx.completed) {
        const outcome = await finalize();
        if (outcome === 'more') {
          dispatch({ type: 'resend' });
          continue;
        }
      }
      dispatch({ type: 'completed', fileUid: ctx.completed.fileUid });
      return;
    }
  }

  // ---- the page around it -------------------------------------------------

  function releaseSentinel(held) {
    if (held && typeof held.release === 'function') Promise.resolve(held.release()).catch(() => null);
  }

  // The browser lets go of the lock whenever the tab is hidden (a held one says
  // released === false), so this asks again when the tab is shown.
  async function acquireWake() {
    if (!wakeLock || typeof wakeLock.request !== 'function') return;
    if (sentinel && sentinel.released === false) return;
    const stale = sentinel;
    sentinel = null;
    releaseSentinel(stale);
    try {
      const next = await wakeLock.request('screen');
      if (isUploadInFlight(state.status)) sentinel = next;
      else releaseSentinel(next);
    } catch (_) {
      // No lock is not a failure.
    }
  }

  function releaseWake() {
    const held = sentinel;
    sentinel = null;
    releaseSentinel(held);
  }

  function onOffline() {
    offline = true;
    dispatch({ type: 'offline' });
    if (inflight) abort(inflight, 'offline');
  }

  function onOnline() {
    offline = false;
    dispatch({ type: 'online' });
    if (gate && state.status !== 'paused_mfa') gate.resolve();
  }

  function onFocus() {
    tryResume();
  }

  function onVisibility() {
    if (env.document && env.document.visibilityState === 'visible') {
      tryResume();
      if (isUploadInFlight(state.status)) acquireWake();
    }
  }

  function attach() {
    if (attached) return;
    attached = true;
    offline = !!(env.navigator && env.navigator.onLine === false);
    if (env.window) {
      env.window.addEventListener('offline', onOffline);
      env.window.addEventListener('online', onOnline);
      env.window.addEventListener('focus', onFocus);
    }
    if (env.document) env.document.addEventListener('visibilitychange', onVisibility);
  }

  function detach() {
    if (!attached) return;
    attached = false;
    if (env.window) {
      env.window.removeEventListener('offline', onOffline);
      env.window.removeEventListener('online', onOnline);
      env.window.removeEventListener('focus', onFocus);
    }
    if (env.document) env.document.removeEventListener('visibilitychange', onVisibility);
  }

  async function run() {
    attach();
    acquireWake();
    try {
      await runFlow();
    } catch (error) {
      if (!(error instanceof Stop)) {
        // Something nothing above expected: the upload ends in words rather than hanging.
        dispatch({ type: 'fail', code: 'unknown', message: null });
      }
    } finally {
      detach();
      releaseWake();
      if (cancelled && ctx.uploadId && !discarded && !released) {
        discarded = true;
        api.discard(ctx.uploadId);
      }
    }
    return snapshot;
  }

  // ---- what a page can ask of it ------------------------------------------

  snapshot = buildSnapshot();
  snapshotKey = `${snapshot.status}|${snapshot.code}|${snapshot.message}|${snapshot.percent}|${snapshot.text}|${snapshot.fileUid}`;

  return {
    /** Begins the upload. Resolves with the last snapshot once it is ready, failed or cancelled. */
    start() {
      if (!running) running = run();
      return running;
    },

    /**
     * After a failure, begins again; keyon carries on a session that is still
     * open. Not after a final one, whatever calls it: the chip offers no Retry
     * there (uploadChipActions), and a new session would send the whole file again.
     */
    retry() {
      if (state.status !== 'failed' || cancelled || !snapshot.actions.includes('retry')) return running;
      ctx.failures = 0;
      ctx.restarts = 0;
      ctx.busyWaits = 0;
      ctx.orderFixes = 0;
      ctx.completed = null;
      ctx.pendingStatus = null;
      ctx.lastFailure = null;
      ctx.inChunk = null;
      dispatch({ type: 'retry' });
      running = run();
      return running;
    },

    /**
     * Stops the upload and lets keyon release it (a session is abandoned, and
     * its video with it while nothing has referenced it). Once; and not after
     * release().
     */
    async cancel() {
      if (cancelled || released) return snapshot;
      cancelled = true;
      dispatch({ type: 'cancel' });
      if (inflight) abort(inflight, 'cancel');
      if (sleeper) {
        timers.clearTimeout(sleeper.id);
        sleeper.reject(new Stop('cancelled'));
        sleeper = null;
      }
      if (gate) gate.reject(new Stop('cancelled'));
      detach();
      releaseWake();
      if (ctx.uploadId && !discarded) {
        discarded = true;
        await api.discard(ctx.uploadId);
      }
      return snapshot;
    },

    /** "Verify again": the code page, in another window, so this one keeps what is being written. */
    verifyAgain() {
      try {
        openVerifyWindow();
      } catch (_) {
        // A blocked window is the person's to allow; the button stays.
      }
    },

    /**
     * Lets go of the upload without releasing the video: it has been sent with
     * a message, which now holds it, so a later cancel must not touch it.
     */
    release() {
      released = true;
      detach();
      releaseWake();
    },

    getSnapshot: () => snapshot,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
